import { useQuery } from "@tanstack/react-query";

import {
  ATTACHMENTS_BUCKET,
  SIGNED_URL_TTL_SECONDS,
  getAttachmentPath,
} from "../providers/commons/attachments";
import { getSupabaseClient } from "../providers/supabase/supabase";

// Refetch a few minutes before the signed URL expires
const SIGNED_URL_STALE_MS = (SIGNED_URL_TTL_SECONDS - 300) * 1000;

/**
 * URL to show an attachment. A file of the (private) bucket gets a signed URL valid for one hour; anything else
 * (a blob:/data: preview of a file not uploaded yet, FakeRest, an external link) keeps its own `src`.
 * Undefined while signing or when signing fails.
 */
export const useAttachmentUrl = (attachment: {
  path?: string | null;
  src?: string | null;
}): string | undefined => {
  const src = attachment.src ?? "";
  const path =
    src.startsWith("blob:") || src.startsWith("data:")
      ? null
      : getAttachmentPath(attachment);
  const { data } = useQuery({
    queryKey: ["attachment-signed-url", path],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .storage.from(ATTACHMENTS_BUCKET)
        .createSignedUrl(path!, SIGNED_URL_TTL_SECONDS);
      if (error) throw error;
      return data.signedUrl;
    },
    enabled: path != null,
    staleTime: SIGNED_URL_STALE_MS,
  });
  return path != null ? data : src || undefined;
};

/**
 * URL to show a logo or seller photo (Block 2A M2b): its own URL (`branding` is public), or a signed one when it is
 * an old public URL of `attachments` saved before that bucket went private. Only the `src` is looked at: a `branding`
 * file also carries a `path`, which must not be signed against `attachments`.
 */
export const useImageUrl = (src?: string | null): string | undefined =>
  useAttachmentUrl({ src });
